import React, { useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router';
import { ProjectId, SpaceTabId } from 'api/models';
import { Space } from 'api/space';
import { Loading } from 'components/atoms/loading';
import { SpaceIcon } from 'components/atoms/space-icon';
import { getProjectSpacesQueryKey, useProjectSpacesQuery } from 'queries';
import {
  useSpaceDeleteMutation,
  useSpaceUpdateMutation,
} from 'queries/project-spaces';
import { getProjectSpacesUrl, getSpaceTabDetailUrl } from 'utils/urls';

export const useSpaceSubMenuActions = ({
  projectId,
  setOpenKeys,
}: {
  projectId: number;
  setOpenKeys: (keys: string[]) => void;
}) => {
  const [editing, setEditing] = useState(false);

  const queryClient = useQueryClient();
  const history = useHistory();
  const { data: projectSpacesList = [] } = useProjectSpacesQuery(
    ProjectId(projectId),
  );

  const { mutate: updateSpaceMutation, isLoading: updatingSpace } =
    useSpaceUpdateMutation({
      onMutate: async (updatedSpace) => {
        const previousSpaces: Space[] =
          queryClient.getQueryData(
            getProjectSpacesQueryKey(ProjectId(projectId)),
          ) ?? [];
        const filteredSpaces = previousSpaces.filter(
          (space) => space.spaceId !== updatedSpace.spaceId,
        );

        await queryClient.setQueryData(
          getProjectSpacesQueryKey(ProjectId(projectId)),
          [...filteredSpaces, updatedSpace],
        );

        return previousSpaces;
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getProjectSpacesQueryKey(ProjectId(projectId)),
        );
      },
      onError: async (err, _, context) => {
        await queryClient.setQueryData(
          getProjectSpacesQueryKey(ProjectId(projectId)),
          context,
        );
      },
    });
  const { mutateAsync: deleteSpacemutation, isLoading: deletingSpace } =
    useSpaceDeleteMutation();

  const onSpaceUpdate = (updatedSpace: Space) => {
    updateSpaceMutation(updatedSpace);
  };

  const onSpaceDelete = async (deletedSpaceId: number) => {
    return deleteSpacemutation(deletedSpaceId, {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getProjectSpacesQueryKey(ProjectId(projectId)),
        );

        return history.push(getProjectSpacesUrl(ProjectId(projectId)));
      },
    });
  };

  const onSpaceDocAdd = async (updatedSpaceId: number) => {
    const newSpaceDoc = {
      space: { spaceId: updatedSpaceId },
      url: '',
      spaceTabId: 'new',
      spaceTabTitle: '',
    };

    const updatedSpaces = projectSpacesList.map((space) => {
      const hasNewDoc = space.spaceTabs.find(
        (spaceTab) => spaceTab.spaceTabId.toString() === 'new',
      );

      if (space.spaceId === updatedSpaceId && !hasNewDoc) {
        return { ...space, spaceTabs: [...space.spaceTabs, newSpaceDoc] };
      }

      return space;
    });

    await queryClient.setQueryData(
      getProjectSpacesQueryKey(ProjectId(projectId)),
      updatedSpaces,
    );

    setOpenKeys([updatedSpaceId.toString()]);

    return history.push(
      getSpaceTabDetailUrl(
        SpaceTabId<string>(projectId, updatedSpaceId, 'new'),
      ),
    );
  };

  const renderIcon = useCallback(
    (_space) => {
      if (deletingSpace || updatingSpace) {
        return <Loading size="small" />;
      }

      return <SpaceIcon name={_space.spaceName} />;
    },
    [deletingSpace, updatingSpace],
  );

  return {
    onSpaceUpdate,
    onSpaceDelete,
    onSpaceDocAdd,
    renderIcon,
    updatingSpace,
    setEditing,
    editing,
  };
};
